import { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { createRecurringTrainingReservations } from "../../api/courts";
import { ICourt } from "../../models/ICourt";
import { IReservationCategory } from "../../models/ICourtReservation";
import { setGeneralAlertMessage } from "../../store/slices/generalSlice";
import { useAppDispatch } from "../../store/store";
import { Alert, Button, DatePicker, Dialog, Field, FormGrid, Input, Select, Textarea } from "../../ui";
import "../ReservationDialog/styles.scss";

type RecurringTrainingDialogProps = {
  courts: ICourt[];
  categories: IReservationCategory[];
  isOpen: boolean;
  onClose: (update?: boolean) => void;
};

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

const RecurringTrainingDialog = (props: RecurringTrainingDialogProps) => {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [formData, setFormData] = useState({
    courtId: "",
    dayOfWeek: 1, // Monday
    startTime: null as Date | null,
    endTime: "" as string,
    startDate: null as Date | null,
    endDate: null as Date | null,
    numberOfWeeks: 4,
    reservationCategoryId: "",
    needBall: false,
    notes: "",
  });

  useEffect(() => {
    if (props.isOpen) {
      setErrorMessage("");

      setFormData({
        courtId: "",
        dayOfWeek: 1,
        startTime: null,
        endTime: "",
        startDate: new Date(),
        endDate: null,
        numberOfWeeks: 4,
        reservationCategoryId: "",
        needBall: false,
        notes: "",
      });
    }
  }, [props.isOpen, props.courts]);

  const handleClose = (update?: boolean) => {
    props.onClose(update);
    setErrorMessage("");
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.courtId) {
      setErrorMessage("Please select a court");
      return;
    }

    // Prevent creating recurring training with inactive courts
    const selectedCourt = props.courts.find((c) => c.id === formData.courtId);
    if (selectedCourt && !selectedCourt.isActive) {
      setErrorMessage("Cannot create recurring training for inactive court");
      return;
    }

    if (!formData.startTime) {
      setErrorMessage("Please select a start time");
      return;
    }

    if (!formData.endTime) {
      setErrorMessage("Please select an end time");
      return;
    }

    if (!formData.startDate) {
      setErrorMessage("Please select a start date");
      return;
    }

    if (!formData.endDate && !formData.numberOfWeeks) {
      setErrorMessage("Please provide either an end date or number of weeks");
      return;
    }

    setLoading(true);
    setErrorMessage("");

    try {
      // Combine the selected time with the start date to create a proper datetime
      const startDateMoment = moment(formData.startDate).startOf("day");
      const startTimeMoment = moment(formData.startTime);

      // Parse end time - may have "next:" prefix for next-day times
      const isNextDay = formData.endTime.startsWith("next:");
      const endTimeStr = isNextDay ? formData.endTime.replace("next:", "") : formData.endTime;
      const [endHour, endMin] = endTimeStr.split(":").map(Number);

      // Create full datetime objects by combining date and time in local timezone
      const startDateTimeLocal = startDateMoment
        .clone()
        .hour(startTimeMoment.hour())
        .minute(startTimeMoment.minute())
        .second(startTimeMoment.second())
        .millisecond(0);

      const endDateTimeLocal = startDateMoment
        .clone()
        .add(isNextDay ? 1 : 0, "day")
        .hour(endHour)
        .minute(endMin)
        .second(0)
        .millisecond(0);

      // Convert to UTC
      const startDateTimeUTC = moment.utc(startDateTimeLocal.toISOString());
      const endDateTimeUTC = moment.utc(endDateTimeLocal.toISOString());

      // Format times as HH:mm:ss in UTC (server expects UTC time)
      const startTimeStr = startDateTimeUTC.format("HH:mm:ss");
      const endTimeUTCStr = endDateTimeUTC.format("HH:mm:ss");

      const requestData: any = {
        courtId: formData.courtId,
        startTime: startTimeStr,
        endTime: endTimeUTCStr,
        dayOfWeek: formData.dayOfWeek,
        startDate: moment(formData.startDate).format("YYYY-MM-DD"),
        notes: formData.notes || undefined,
        needBall: false,
        reservationCategoryId: formData.reservationCategoryId || undefined,
      };

      if (formData.endDate) {
        requestData.endDate = moment(formData.endDate).format("YYYY-MM-DD");
      } else if (formData.numberOfWeeks) {
        requestData.numberOfWeeks = formData.numberOfWeeks;
      }

      await createRecurringTrainingReservations(requestData);
      dispatch(
        setGeneralAlertMessage({ message: "Recurring training reservations created successfully", type: "success" }),
      );
      handleClose(true);
    } catch (error: any) {
      setErrorMessage(error?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Generate end time options (15-minute intervals, up to 2:00 AM next day)
  const endTimeOptions = useMemo(() => {
    const options: string[] = [];
    // From 00:00 to 23:45
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        options.push(`${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`);
      }
    }
    return options;
  }, []);

  // Filter end time options based on start time
  const filteredEndTimeOptions = useMemo(() => {
    if (!formData.startTime) return endTimeOptions;
    const startMoment = moment(formData.startTime);
    const startHour = startMoment.hour();
    const startMin = startMoment.minute();
    const startMinutes = startHour * 60 + startMin;

    // Show times after start time on same day, plus 00:00-02:00 as next day
    const sameDayOptions = endTimeOptions.filter((time) => {
      const [h, m] = time.split(":").map(Number);
      return h * 60 + m > startMinutes;
    });

    // Add next day options (00:00 to 02:00)
    const nextDayOptions: string[] = [];
    for (let hour = 0; hour <= 2; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        if (hour === 2 && minute > 0) break;
        nextDayOptions.push(`${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`);
      }
    }

    return [...sameDayOptions, ...nextDayOptions.map((t) => `next:${t}`)];
  }, [formData.startTime, endTimeOptions]);

  // Sort courts by name
  const sortedCourts = [...props.courts].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Dialog
      open={props.isOpen}
      onClose={() => handleClose()}
      placement="side"
      title="Create Recurring Training"
      busy={loading}
      footer={
        <>
          <Button variant="ghost" onClick={() => handleClose()} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Create Recurring Training
          </Button>
        </>
      }
    >
      <div className="rsv-form">
        {errorMessage && <Alert tone="danger">{errorMessage}</Alert>}

        <Field label="Court" required>
          {(id) => (
            <Select id={id} block value={formData.courtId} onChange={(e) => handleChange("courtId", e.target.value)} disabled={loading}>
              <option value="">Select a court</option>
              {sortedCourts.map((court) => {
                const isDisabled = !court.isActive;
                return (
                  <option key={court.id} value={court.id} disabled={isDisabled}>
                    {court.name} {!court.isActive ? "(Inactive)" : ""}
                  </option>
                );
              })}
            </Select>
          )}
        </Field>

        <Field label="Category">
          {(id) => (
            <Select
              id={id}
              block
              value={formData.reservationCategoryId}
              onChange={(e) => handleChange("reservationCategoryId", e.target.value)}
              disabled={loading}
            >
              <option value="">No category</option>
              {props.categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name} (EGP {cat.pricePerHour}/hr)
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field label="Day of Week" required>
          {(id) => (
            <Select id={id} block value={formData.dayOfWeek} onChange={(e) => handleChange("dayOfWeek", parseInt(e.target.value))} disabled={loading}>
              {DAYS_OF_WEEK.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <FormGrid columns={2}>
          <Field label="Start Time" required>
            {(id) => (
              <DatePicker
                id={id}
                selected={formData.startTime}
                onChange={(date) => handleChange("startTime", date)}
                mode="time"
                timeIntervals={15}
                dateFormat="hh:mm aa"
                placeholderText="Select start time"
                disabled={loading}
              />
            )}
          </Field>

          <Field label="End Time" required hint={formData.endTime.startsWith("next:") ? "End time will be on the next day" : undefined}>
            {(id) => (
              <Select
                id={id}
                block
                value={formData.endTime}
                onChange={(e) => handleChange("endTime", e.target.value)}
                disabled={loading || !formData.startTime}
              >
                <option value="">Select end time</option>
                {filteredEndTimeOptions.map((time) => {
                  const isNext = time.startsWith("next:");
                  const raw = isNext ? time.replace("next:", "") : time;
                  const [hours, minutes] = raw.split(":").map(Number);
                  const date = new Date();
                  date.setHours(hours, minutes, 0, 0);
                  const displayTime = moment(date).format("hh:mm A");
                  return (
                    <option key={time} value={time}>
                      {displayTime} {isNext ? "(next day)" : ""}
                    </option>
                  );
                })}
              </Select>
            )}
          </Field>
        </FormGrid>

        <Field label="Start Date" required>
          {(id) => (
            <DatePicker
              id={id}
              selected={formData.startDate}
              onChange={(date) => handleChange("startDate", date)}
              dateFormat="MMM dd, yyyy"
              placeholderText="Select start date"
              disabled={loading}
              minDate={new Date()}
            />
          )}
        </Field>

        <FormGrid columns={2}>
          <Field label="End Date">
            {(id) => (
              <DatePicker
                id={id}
                selected={formData.endDate}
                onChange={(date) => handleChange("endDate", date)}
                dateFormat="MMM dd, yyyy"
                placeholderText="Select end date (optional)"
                disabled={loading}
                minDate={formData.startDate || new Date()}
              />
            )}
          </Field>

          <Field label="Number of Weeks" hint={formData.endDate ? "End date takes precedence" : "Used if end date is not provided"}>
            {(id) => (
              <Input
                id={id}
                type="number"
                min="1"
                max="52"
                value={formData.numberOfWeeks}
                onChange={(e) => handleChange("numberOfWeeks", parseInt(e.target.value) || 0)}
                disabled={loading || !!formData.endDate}
                placeholder="Enter number of weeks"
              />
            )}
          </Field>
        </FormGrid>

        <Field label="Notes">
          {(id) => (
            <Textarea
              id={id}
              placeholder="Additional notes (optional)"
              value={formData.notes}
              onChange={(e) => handleChange("notes", e.target.value)}
              rows={3}
              disabled={loading}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
};

export default RecurringTrainingDialog;
